import { FC } from 'react';
import { useFragment } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import MenuIcon from '@mui/icons-material/Menu';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import Paper from '@mui/material/Paper';
import TableRow from '@mui/material/TableRow';
import Sorting, { useSorting } from 'components/Sorting';
import Table from 'components/Table';
import TableCell from 'components/TableCell';
import MenuItemsListRow from 'components/MenuItemsListRow';
import FabDialog from 'components/FabDialog';
import AddMenuItemDialog from 'components/AddMenuItemDialog';

import { useDelete } from 'lib';

import { MenuItemsList_menu$key } from '__generated__/MenuItemsList_menu.graphql';
import { MenuItemsListSortMutation } from '__generated__/MenuItemsListSortMutation.graphql';

type Props = {
  menu: MenuItemsList_menu$key;
};

const MenuItemsList: FC<Props> = props => {
  const menu = useFragment(
    graphql`
      fragment MenuItemsList_menu on Menu {
        menuItems(first: 2147483647) {
          __id
          edges {
            node {
              id

              ...MenuItemsListRow_menuItem
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }

        ...AddMenuItemDialog_menu
      }
    `,
    props.menu
  );

  const connectionId = menu.menuItems?.__id;

  const [sort, sorting] = useSorting<MenuItemsListSortMutation>(
    graphql`
      mutation MenuItemsListSortMutation(
        $positions: [EntityWithPositionInput!]!
      ) {
        updateMenuItemPositions(positions: $positions)
      }
    `,
    connectionId
  );

  const [deleteMenuItem, deleting] = useDelete(connectionId);

  const { edges, pageInfo } = menu.menuItems ?? {};

  if (!edges) return null;

  return (
    <Paper sx={{ p: 2 }}>
      <Sorting edges={edges} onSort={sort}>
        <Table
          pagination
          pageInfo={pageInfo ?? null}
          icon={<MenuIcon />}
          loading={deleting || sorting}
          head={
            <TableRow>
              <TableCell sx={{ width: '15px' }} />
              <TableCell>Pavadinimas</TableCell>
              <TableCell center>Tipas</TableCell>
              <TableCell />
            </TableRow>
          }>
          {edges?.map(({ node }) => (
            <MenuItemsListRow
              key={node.id}
              menuItem={node}
              onDelete={deleteMenuItem}
            />
          ))}
        </Table>
      </Sorting>

      {connectionId && (
        <FabDialog fullWidth maxWidth="sm" icon={<PlaylistAddIcon />}>
          <AddMenuItemDialog connectionId={connectionId} menu={menu} />
        </FabDialog>
      )}
    </Paper>
  );
};

export default MenuItemsList;
